<template>
  <!-- The main div now has padding-top to create space instead of a spacer div -->
  <div class="min-h-screen flex flex-col items-center pt-16 md:pt-24" style="background: linear-gradient(rgba(74, 222, 128, 0.8), rgba(248, 113, 113, 0.9), rgba(252, 211, 77, 0.7)), url('assets/images/fake4.png') center/cover no-repeat, #f9fafb">
    
    <!-- The redundant Navbar Spacer has been REMOVED from here -->

    <!-- Logo -->
    <img
      src="/assets/images/CHECK-APP.png"
      alt="Logo"
      class="mb-6 w-48 md:w-64 transition-all duration-300 z-10"
    />

    <!-- Search Bar -->
    <div class="w-full relative text-gray-600 focus-within:text-gray-900 z-20 px-4">
      <div class="flex justify-center">
        <div class="w-full max-w-3xl search-bar-landing" :class="{ 'search-bar-landing-open': showSuggestions && (filteredSuggestions.length > 0 || (searchTerm.length === 0 && recentSearches.length > 0) || isLoading) }">
          <div class="search-bar-inner">
          <!-- Search icon (left) -->
          <span class="search-bar-search-icon">
            <button @click="performSearch" type="submit" class="search-icon-btn">
              <svg focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20"><path fill="#9aa0a6" d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path></svg>
            </button>
          </span>
          <!-- Input -->
          <input
            ref="searchInput"
            v-model="searchTerm"
            @input="handleSearchInput"
            @keyup.enter="performSearch"
            @keydown.up.prevent="navigateSuggestions(-1)"
            @keydown.down.prevent="navigateSuggestions(1)"
            @focus="showSuggestions = true"
            @blur="handleBlur"
            type="search"
            name="q"
            class="search-bar-input"
            :placeholder="$t('search.placeholder')"
            autocomplete="off"
          />
          <!-- Right actions: clear | divider | chatbot -->
          <div class="search-bar-right">
            <button
              v-if="searchTerm"
              @click="clearSearch"
              class="search-bar-clear-btn"
            >
              <svg focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><path fill="#70757a" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path></svg>
            </button>
            <span class="search-bar-divider"></span>
            <button
              @click="goToChatbot"
              class="search-bar-chatbot-btn"
              :title="$t('search.askChatbot') || 'Ask the AI Chatbot'"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><circle cx="9" cy="10" r="1" fill="currentColor" stroke="none"/><circle cx="15" cy="10" r="1" fill="currentColor" stroke="none"/></svg>
            </button>
          </div>
          </div>
        </div>
      </div>

      <!-- Search Suggestions Dropdown — Google Style -->
      <div
        v-if="showSuggestions && !showBlacklistModal && !showAuthenticModal && !showNoResultModal"
        class="suggestions-panel"
      >
        <!-- Recent Searches -->
        <div
          v-if="searchTerm.length === 0 && recentSearches.length > 0"
        >
          <div class="suggestions-section-label">Recent searches</div>
          <ul>
            <li
              v-for="(search, index) in recentSearches"
              :key="`recent-${index}`"
              class="suggestion-row"
              :class="{ 'suggestion-row-active': activeSuggestionIndex === index }"
              @mousedown="selectRecentSearch(search)"
              @mouseenter="activeSuggestionIndex = index"
            >
              <i class="fas fa-history suggestion-row-icon"></i>
              <span class="suggestion-row-text truncate">{{ search }}</span>
              <button
                @click.stop="removeRecentSearch(index)"
                class="suggestion-row-remove"
              >
                <i class="fas fa-times"></i>
              </button>
            </li>
          </ul>
        </div>
        
        <!-- Search Results Area -->
        <div v-else>
          <!-- Loading -->
          <div v-if="isLoading" class="suggestion-loading">
            <i class="fas fa-spinner fa-spin mr-2"></i>
            <span v-if="isUrlOrPhone">Verifying...</span>
            <span v-else>{{ $t('searching') || 'Searching...' }}</span>
          </div>

          <!-- Suggestions List -->
          <div
            v-else-if="filteredSuggestions.length > 0"
            ref="suggestionsList"
          >
            <ul>
              <li
                v-for="(suggestion, index) in filteredSuggestions"
                :key="suggestion.uniqueId"
                class="suggestion-row"
                :class="{ 'suggestion-row-active': activeSuggestionIndex === index }"
                @mousedown="selectSuggestion(suggestion)"
                @mouseenter="activeSuggestionIndex = index"
              >
                <div class="suggestion-img-wrap">
                  <img
                    :src="suggestion.imageUrl"
                    :alt="suggestion.name"
                    class="suggestion-img"
                    @error="handleImageError"
                  />
                </div>
                <div class="suggestion-body">
                  <div class="suggestion-header">
                    <span class="suggestion-name">{{ suggestion.name }}</span>
                    <span v-if="isCleanValue(suggestion.displayLabel) && suggestion.displayLabel !== suggestion.type" class="suggestion-badge">{{ suggestion.displayLabel }}</span>
                  </div>
                  <!-- Official subtitle (title + org) -->
                  <div v-if="suggestion.type === 'Official' && isCleanValue(suggestion.displayLabel)" class="suggestion-subtitle">
                    <span class="suggestion-title-tag">{{ suggestion.displayLabel }}</span>
                    <span v-if="suggestion.description && !isNaNOrNone(suggestion.description)"> · {{ suggestion.description }}</span>
                  </div>
                  <!-- Standard description -->
                  <p v-else-if="suggestion.description && !isNaNOrNone(suggestion.description)" class="suggestion-desc">
                    {{ suggestion.description }}
                  </p>
                </div>
              </li>
            </ul>
            <!-- See all results -->
            <div
              class="suggestion-row suggestion-see-all"
              @mousedown="performSearch()"
            >
              <div class="suggestion-see-all-icon">
                <i class="fas fa-search"></i>
              </div>
              <span class="suggestion-see-all-text">{{ $t('seeAllResults') || 'See all results for' }} "<strong>{{ searchTerm.trim() }}</strong>"</span>
            </div>
          </div>

          <!-- No Results -->
          <div v-else-if="searchTerm.length > 0 && !isLoading" class="suggestion-empty">
            {{ $t('no_results_message') || 'No results found for' }} "{{ searchTerm }}"
          </div>
        </div>
      </div>
    </div>

    <!-- Modals with Blurred Background -->
    <div v-if="showBlacklistModal" class="fixed inset-0 bg-gray-100 bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
        <div class="bg-white rounded-lg shadow-2xl max-w-lg w-full text-center p-6 md:p-8">
            <div class="text-red-500 mb-4"><i class="fas fa-exclamation-triangle fa-4x"></i></div>
            <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{{ $t('search.maliciousWarningTitle') }}</h2>
            <div v-if="blacklistResult" class="text-left bg-red-50 p-4 rounded-lg border border-red-200 space-y-3">
                <p class="text-gray-700 leading-relaxed" v-html="$t('search.maliciousWarningBody', { resourceName: blacklistResult.name.replace('Blacklisted: ', ''), institution: blacklistResult.related_data.institution, authenticity: blacklistResult.related_data.authenticity })"></p>

                <!-- Description if available -->
                <div v-if="blacklistResult.related_data.description" class="bg-white rounded p-3 border border-red-100">
                    <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{{ $t('search.description') || 'Details' }}</p>
                    <p class="text-sm text-gray-700">{{ blacklistResult.related_data.description }}</p>
                </div>

                <!-- Number of times reported -->
                <div v-if="blacklistResult.related_data.occurrence_count > 1" class="flex items-center gap-2 bg-red-100 rounded p-2">
                    <i class="fas fa-flag text-red-600 text-sm"></i>
                    <p class="text-sm font-semibold text-red-800">
                        {{ $t('search.reportedTimes', { count: blacklistResult.related_data.occurrence_count }) || `Reported ${blacklistResult.related_data.occurrence_count} times` }}
                    </p>
                </div>

                <p class="mt-4 text-sm font-semibold text-gray-800">{{ $t('search.maliciousWarningAdvice') }}</p>
            </div>
            <button @click="closeBlacklistModal" class="mt-6 w-full bg-red-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-red-700 transition-colors duration-300">{{ $t('search.understandAndClose') }}</button>
        </div>
    </div>

    <div v-if="showAuthenticModal" class="fixed inset-0 bg-gray-100 bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
        <div class="bg-white rounded-lg shadow-2xl max-w-lg w-full text-center p-6 md:p-8">
            <div class="text-green-500 mb-4"><i class="fas fa-check-circle fa-4x"></i></div>
            <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{{ $t('search.verifiedTitle') }}</h2>
            <div v-if="authenticResult" class="text-left bg-green-50 p-4 rounded-lg border border-green-200">
                <p class="text-gray-700 leading-relaxed" v-html="$t('search.verifiedBody', { resourceName: authenticResult.name, institution: authenticResult.related_data.institution, authenticity: authenticResult.related_data.authenticity })"></p>
                <p class="mt-4 text-sm font-semibold text-gray-800">{{ $t('search.verifiedAdvice') }}</p>
            </div>
            <button @click="closeAuthenticModal" class="mt-6 w-full bg-green-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-green-700 transition-colors duration-300">{{ $t('search.continue') }}</button>
        </div>
    </div>

    <div v-if="showNoResultModal" class="fixed inset-0 bg-gray-100 bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
        <div class="bg-white rounded-lg shadow-2xl max-w-lg w-full text-center p-6 md:p-8">
            <div class="text-yellow-500 mb-4"><i class="fas fa-question-circle fa-4x"></i></div>
            <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{{ $t('search.verificationNeededTitle') }}</h2>
            <div class="text-left bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <p class="text-gray-700 leading-relaxed" v-html="$t('search.verificationNeededBody', { searchTerm: searchTerm })"></p>
                <p class="mt-2 text-gray-700 leading-relaxed">{{ $t('search.verificationNeededAdvice') }}</p>
            </div>
            <button @click="closeNoResultModal" class="mt-6 w-full bg-yellow-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-yellow-600 transition-colors duration-300">{{ $t('search.understandAndBeCareful') }}</button>
        </div>
    </div>

  </div>
  <BeforeFooter class="mt-auto" />
</template>

<script>
import BeforeFooter from '@/components/common/BeforeFooter.vue';
import axios from 'axios';
import { useSearchStore } from '@/stores/search';
import { debounce } from 'lodash';

const BaseURL = process.env.VUE_APP_API_BASE_URL;
const RECENT_SEARCHES_KEY = 'app_recent_searches';
const MAX_RECENT_SEARCHES = 5;

function loadRecentSearches() {
  try {
    const raw = sessionStorage.getItem(RECENT_SEARCHES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENT_SEARCHES) : [];
  } catch {
    return [];
  }
}

function saveRecentSearches(searches) {
  try {
    sessionStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
  } catch {
    // silent
  }
}

export default {
  components: { BeforeFooter },
  name: "LandingPage",
  data() {
    return {
      searchTerm: "",
      searchSuggestions: [],
      recentSearches: loadRecentSearches(),
      showSuggestions: false,
      isLoading: false,
      activeSuggestionIndex: -1,
      visibleSuggestions: 5,
      blurTimeout: null,
      showBlacklistModal: false,
      blacklistResult: null,
      showAuthenticModal: false,
      authenticResult: null,
      showNoResultModal: false,
      searchMatchedLang: null,
    };
  },
  computed: {
    filteredSuggestions() {
      return this.searchSuggestions.filter(item => {
        if (!item.name || !item.name.trim()) return false;
        const nameLower = item.name.toLowerCase().trim();
        if (nameLower === 'nan' || nameLower === 'none' || nameLower === 'null') return false;
        return true;
      });
    },
    isUrlOrPhone() {
      if (!this.searchTerm) return false;
      const term = this.searchTerm.trim();
      const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      const phoneRegex = /^\+?[0-9\s-()]{7,}$/;
      return urlRegex.test(term) || phoneRegex.test(term);
    }
  },
  created() {
    this.debouncedSearch = debounce(this.fetchSuggestions, 300);
  },
  methods: {
    toTitleCase(str) {
        if (!str || typeof str !== 'string') return str;
        return str.toLowerCase().split(' ').map(word => {
            if (word.length === 0) return '';
            return word.charAt(0).toUpperCase() + word.slice(1);
        }).join(' ');
    },

    isNaNOrNone(value) {
      if (value === null || value === undefined) return true;
      const val = String(value).toLowerCase().trim();
      return val === 'nan' || val === 'none' || val === 'null' || val === 'n/a';
    },

    isCleanValue(val) {
      if (val === null || val === undefined) return false;
      const s = String(val).toLowerCase().trim();
      return s !== '' && s !== 'nan' && s !== 'none' && s !== 'null' && s !== 'undefined' && s !== 'n/a';
    },

    getSuggestionIconClass(suggestion) {
      const iconMap = {
        'Organization': 'fas fa-building',
        'Official':     'fas fa-user-tie',
        'Embassy':      'fas fa-globe',
        'Ambassador':   'fas fa-globe',
        'Bank':         'fas fa-landmark',
        'School':       'fas fa-graduation-cap',
        'Hospital':     'fas fa-heartbeat',
      };
      return iconMap[suggestion.type] || 'fas fa-building';
    },

    getFullImageUrl(path) {
      if (!path || typeof path !== 'string') return null;
      if (path.startsWith('http')) return path;
      return `${BaseURL}${path.startsWith('/') ? '' : '/'}${path}`;
    },

    normalizeSuggestion(item) {
        const typeMap = {
            'Structure': 'Organization', 'Boss': 'Official', 'Embassy': 'Embassy',
            'Ambassador': 'Ambassador', 'PublicPersonality': 'Official', 'Bank': 'Bank',
            'School': 'School', 'Hospital': 'Hospital', 'StatePersonnels': 'Official',
        };

        const cleanVal = (v) => {
          if (v === null || v === undefined) return null;
          const s = String(v).trim();
          const lower = s.toLowerCase();
          if (lower === 'nan' || lower === 'none' || lower === 'null' || lower === '' || lower === 'n/a') return null;
          return s;
        };

        const suggestion = {
            uniqueId: `${item.model_type}-${item.id}`,
            id: item.id,
            name: item.name,
            type: typeMap[item.model_type] || item.model_type,
            displayLabel: typeMap[item.model_type] || item.model_type,
            description: cleanVal(item.description),
            match_score: item.match_score,
            modelType: item.model_type,
            routePath: item.route_path,
            routeParams: item.route_params,
            imageUrl: null,
        };

        const structure = item.related_data?.structure;

        switch (item.model_type) {
            case 'Structure':
                suggestion.name = structure?.name || item.name;
                suggestion.description = cleanVal(structure?.description);
                suggestion.displayLabel = cleanVal(structure?.abbreviation) || suggestion.type;
                suggestion.imageUrl = this.getFullImageUrl(structure?.photo) || '/assets/images/default-ministry.png';
                break;
            case 'Boss':
            case 'StatePersonnels':
                suggestion.name = item.name;
                suggestion.description = this.toTitleCase(structure?.name);
                suggestion.displayLabel = cleanVal(item.title) || suggestion.type;
                suggestion.imageUrl = this.getFullImageUrl(item.photo) || '/assets/images/default-avatar.png';
                break;
            case 'Ambassador':
            case 'PublicPersonality':
                suggestion.description = cleanVal(item.title);
                suggestion.displayLabel = cleanVal(item.title) || suggestion.type;
                suggestion.imageUrl = this.getFullImageUrl(item.photo) || '/assets/images/default-avatar.png';
                break;
            case 'Embassy':
                suggestion.description = `Embassy of ${item.country}`;
                suggestion.displayLabel = suggestion.type;
                suggestion.imageUrl = this.getFullImageUrl(item.photo) || '/assets/images/default-embassy.png';
                break;
            default:
                suggestion.displayLabel = suggestion.type;
                suggestion.imageUrl = this.getFullImageUrl(item.photo) || '/assets/images/default-ministry.png';
                break;
        }

        return suggestion;
    },

    handleImageError(event) {
        event.target.src = '/assets/images/default-avatar.png';
    },

    getSuggestionTypeClass(type) {
        const classes = {
            'Organization': 'bg-blue-100 text-blue-800', 'Official': 'bg-green-100 text-green-800',
            'Embassy': 'bg-red-100 text-red-800', 'Ambassador': 'bg-yellow-100 text-yellow-800',
            'Bank': 'bg-indigo-100 text-indigo-800', 'School': 'bg-purple-100 text-purple-800',
            'Hospital': 'bg-pink-100 text-pink-800',
        };
        return classes[type] || 'bg-gray-100 text-gray-800';
    },

    handleBlur() {
      this.blurTimeout = setTimeout(() => { this.showSuggestions = false; }, 200);
    },

    handleSearchInput() {
      if (!this.searchTerm.trim()) {
        this.isLoading = false;
        this.searchSuggestions = [];
        this.activeSuggestionIndex = -1;
        this.showSuggestions = true;
        return;
      }
      this.isLoading = true;
      this.debouncedSearch();
    },

    async fetchSuggestions() {
      if (!this.searchTerm.trim()) {
          this.isLoading = false;
          return;
      }

      const currentLang = this.$i18n.locale;
      const altLang = currentLang === 'fr' ? 'en' : 'fr';

      try {
        const [primaryRes, altRes] = await Promise.allSettled([
          axios.get(`${BaseURL}/search/`, {
            params: { search_term: this.searchTerm, quick_search: true },
            headers: { 'Accept-Language': currentLang }
          }),
          axios.get(`${BaseURL}/search/`, {
            params: { search_term: this.searchTerm, quick_search: true },
            headers: { 'Accept-Language': altLang }
          }),
        ]);

        const primaryData = primaryRes.status === 'fulfilled' ? primaryRes.value.data : null;
        const altData = altRes.status === 'fulfilled' ? altRes.value.data : null;

        const checkBlacklist = (data) => {
          const results = data?.results;
          if (results?.BlacklistedResource) {
            const item = results.BlacklistedResource.items[0];
            const auth = (item.related_data?.authenticity || '').toLowerCase().trim();
            if (auth === 'authentic' || auth === 'authentique') {
              this.authenticResult = { ...item };
              this.showAuthenticModal = true;
            } else {
              this.blacklistResult = item;
              this.showBlacklistModal = true;
            }
            return true;
          }
          return false;
        };

        if (primaryData && checkBlacklist(primaryData)) {
          this.searchSuggestions = [];
          return;
        }

        const primarySuggestions = this.extractSuggestions(primaryData?.results);
        const altSuggestions = this.extractSuggestions(altData?.results);

        const primaryTopScore = primarySuggestions.length > 0 ? (primarySuggestions[0].match_score || 0) : 0;
        const altTopScore = altSuggestions.length > 0 ? (altSuggestions[0].match_score || 0) : 0;

        if (primarySuggestions.length === 0 && altSuggestions.length > 0) {
          this.searchMatchedLang = altLang;
        } else if (altTopScore > primaryTopScore + 15 && altSuggestions.length > 0) {
          this.searchMatchedLang = altLang;
        } else {
          this.searchMatchedLang = currentLang;
        }

        const bestByKey = new Map();
        for (const item of [...primarySuggestions, ...altSuggestions]) {
          const existing = bestByKey.get(item.uniqueId);
          if (!existing || (item.match_score || 0) > (existing.match_score || 0)) {
            bestByKey.set(item.uniqueId, item);
          }
        }
        const merged = Array.from(bestByKey.values());

        this.searchSuggestions = merged
          .sort((a, b) => (b.match_score || 0) - (a.match_score || 0))
          .slice(0, 2);

        this.activeSuggestionIndex = -1;

        if (this.isUrlOrPhone && this.searchSuggestions.length === 0) {
          this.showNoResultModal = true;
        }
      } catch {
          if (this.isUrlOrPhone) this.showNoResultModal = true;
      } finally {
          this.isLoading = false;
      }
    },

    extractSuggestions(results) {
        const allSuggestions = [];
        if (!results) return allSuggestions;

        for (const key in results) {
            if (results[key] && Array.isArray(results[key].items)) {
                results[key].items.forEach(item => {
                    if (item.name || item.title) {
                        allSuggestions.push(this.normalizeSuggestion(item));
                    }
                });
            }
        }
        return allSuggestions.sort((a, b) => (b.match_score || 0) - (a.match_score || 0)).slice(0, 8);
    },

    navigateSuggestions(direction) {
      if (!this.showSuggestions) return;
      const suggestions = this.searchTerm.length === 0 ? this.recentSearches : this.filteredSuggestions;
      if (suggestions.length === 0) return;

      let newIndex = this.activeSuggestionIndex + direction;
      if (newIndex < 0) newIndex = suggestions.length - 1;
      if (newIndex >= suggestions.length) newIndex = 0;
      this.activeSuggestionIndex = newIndex;

      this.$nextTick(() => {
          const listEl = this.$refs.suggestionsList;
          if (listEl && listEl.querySelectorAll('li')[newIndex]) {
            listEl.querySelectorAll('li')[newIndex].scrollIntoView({ block: 'nearest' });
          }
      });
    },

    selectSuggestion(suggestion) {
        if (!suggestion) {
            if (this.activeSuggestionIndex >= 0 && this.filteredSuggestions.length > this.activeSuggestionIndex) {
                suggestion = this.filteredSuggestions[this.activeSuggestionIndex];
            } else {
                return;
            }
        }
        const finalSearchTerm = suggestion.name;
        this.searchTerm = finalSearchTerm;
        this.addToRecentSearches(finalSearchTerm);

        const store = useSearchStore();
        store.searchTerm = finalSearchTerm;

        const navLang = this.searchMatchedLang || this.$i18n.locale;
        if (this.searchMatchedLang && this.searchMatchedLang !== this.$i18n.locale) {
          this.$i18n.locale = this.searchMatchedLang;
          localStorage.setItem('user-language', this.searchMatchedLang);
        }

        this.$router.push({ 
            name: 'search-results', 
            params: { lang: navLang },
            query: { q: finalSearchTerm }
        });
    },

    selectRecentSearch(search) {
      this.searchTerm = search;
      this.performSearch(true);
    },

    addToRecentSearches(term) {
      if (!term.trim()) return;
      const cleanedTerm = term.trim();
      this.recentSearches = this.recentSearches.filter(t => t.toLowerCase() !== cleanedTerm.toLowerCase());
      this.recentSearches.unshift(cleanedTerm);
      if (this.recentSearches.length > MAX_RECENT_SEARCHES) this.recentSearches.pop();
      saveRecentSearches(this.recentSearches);
    },

    performSearch(isFromRecent = false) {
      if (!this.searchTerm.trim()) return;

      if (isFromRecent || this.activeSuggestionIndex < 0) {
        const finalSearchTerm = this.searchTerm.trim();
        this.addToRecentSearches(finalSearchTerm);

        const store = useSearchStore();
        store.searchTerm = finalSearchTerm;

        const navLang = this.searchMatchedLang || this.$i18n.locale;
        if (this.searchMatchedLang && this.searchMatchedLang !== this.$i18n.locale) {
          this.$i18n.locale = this.searchMatchedLang;
          localStorage.setItem('user-language', this.searchMatchedLang);
        }

        this.$router.push({ 
            name: 'search-results', 
            params: { lang: navLang },
            query: { q: finalSearchTerm }
        });
      } else {
        this.selectSuggestion();
      }
    },

    clearSearch() {
      this.searchTerm = '';
      this.searchSuggestions = [];
      this.activeSuggestionIndex = -1;
      this.showBlacklistModal = false; this.blacklistResult = null;
      this.showAuthenticModal = false; this.authenticResult = null;
      this.showNoResultModal = false;
      this.$nextTick(() => {
        this.showSuggestions = true;
        this.$refs.searchInput?.focus();
      });
    },

    removeRecentSearch(index) {
      this.recentSearches.splice(index, 1);
      saveRecentSearches(this.recentSearches);
    },

    closeBlacklistModal() { this.showBlacklistModal = false; this.clearSearch(); },
    closeAuthenticModal() { this.showAuthenticModal = false; this.clearSearch(); },
    closeNoResultModal() { this.showNoResultModal = false; this.clearSearch(); },

    goToChatbot() {
      const lang = this.$i18n.locale || 'en';
      this.$router.push({ name: 'chatbot', params: { lang } });
    }
  },
  beforeUnmount() {
    if (this.blurTimeout) clearTimeout(this.blurTimeout);
  },
  watch: {
    '$i18n.locale'() {
      if (this.searchTerm.trim()) this.fetchSuggestions();
    }
  }
};
</script>

<style scoped>
/* ===== GOOGLE-STYLE SEARCH BAR ===== */
.search-bar-landing {
  position: relative;
}
.search-bar-inner {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 28px;
  border: 1px solid #dfe1e5;
  box-shadow: 0 2px 5px 1px rgba(64,60,67,0.16);
  transition: box-shadow 0.2s, border-radius 0.2s, border-color 0.2s;
  height: 64px;
}
.search-bar-inner:hover {
  box-shadow: 0 2px 8px 1px rgba(64,60,67,0.24);
}
.search-bar-inner:focus-within {
  box-shadow: 0 2px 8px 1px rgba(64,60,67,0.24);
  border-color: #dfe1e5;
}
.search-bar-landing-open .search-bar-inner {
  border-radius: 28px 28px 0 0;
  border-bottom-color: transparent;
  box-shadow: 0 2px 8px 1px rgba(64,60,67,0.24);
}

/* Search icon (left side) */
.search-bar-search-icon {
  display: flex;
  align-items: center;
  padding-left: 14px;
  flex-shrink: 0;
}
.search-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  outline: none;
}
.search-icon-btn:hover svg path {
  fill: #4285f4;
}

/* Input field */
.search-bar-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0 12px;
  font-size: 16px;
  color: #202124;
  background: transparent;
  border: none;
  outline: none;
}
.search-bar-input::placeholder {
  color: #9aa0a6;
}
.search-bar-input::-webkit-search-cancel-button {
  -webkit-appearance: none;
  display: none;
}

/* Right actions container */
.search-bar-right {
  display: flex;
  align-items: center;
  padding-right: 8px;
  gap: 2px;
  flex-shrink: 0;
}

/* Clear (X) button */
.search-bar-clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 0.15s;
}
.search-bar-clear-btn:hover {
  background-color: #f1f3f4;
}
.search-bar-clear-btn:hover svg path {
  fill: #202124;
}

/* Vertical divider between clear and chatbot */
.search-bar-divider {
  width: 1px;
  height: 24px;
  background: #dadce0;
  margin: 0 4px;
  flex-shrink: 0;
}

/* Chatbot button (right side) */
.search-bar-chatbot-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 50%;
  color: #4285f4;
  transition: background-color 0.15s, color 0.15s, transform 0.15s;
}
.search-bar-chatbot-btn:hover {
  background-color: #e8f0fe;
  color: #1967d2;
  transform: scale(1.12);
}

/* ===== SUGGESTIONS PANEL ===== */
.suggestions-panel {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 48rem;
  background: #fff;
  border-radius: 0 0 28px 28px;
  box-shadow: 0 4px 12px rgba(32,33,36,0.2);
  border: 1px solid #e8eaed;
  border-top: none;
  z-index: 30;
  overflow: hidden;
  max-height: 480px;
  overflow-y: auto;
}
.suggestions-panel::before {
  content: '';
  display: block;
  height: 1px;
  background: #ebebeb;
  margin: 0 16px;
}

/* Section label */
.suggestions-section-label {
  padding: 12px 20px 6px;
  font-size: 11px;
  font-weight: 600;
  color: #70757a;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ===== SUGGESTION ROWS ===== */
.suggestion-row {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 16px;
  cursor: pointer;
  gap: 12px;
  transition: background-color 0.12s ease;
  border: none;
  background: transparent;
  text-align: left;
}
.suggestion-row:hover,
.suggestion-row-active {
  background-color: #f8f9fa;
}

/* Image */
.suggestion-img-wrap {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  background: #f1f3f4;
  border: 2px solid #e8eaed;
  transition: border-color 0.15s;
}
.suggestion-row:hover .suggestion-img-wrap,
.suggestion-row-active .suggestion-img-wrap {
  border-color: #c6c9cc;
}
.suggestion-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Body (text area) */
.suggestion-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

/* Header: name + badge */
.suggestion-header {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

/* Name */
.suggestion-name {
  font-size: 15px;
  color: #1a1a1a;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.35;
}

/* Badge (abbreviation) */
.suggestion-badge {
  font-size: 11px;
  color: #1967d2;
  background: #e8f0fe;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.4;
}

/* Official subtitle line (title · org) */
.suggestion-subtitle {
  font-size: 13px;
  color: #5f6368;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.35;
}
.suggestion-title-tag {
  font-weight: 500;
  color: #3c4043;
}

/* Description (2-line clamp) */
.suggestion-desc {
  font-size: 13px;
  color: #70757a;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0;
}

/* ===== RECENT SEARCHES ===== */
.suggestion-row-icon {
  width: 20px;
  flex-shrink: 0;
  text-align: center;
  color: #9aa0a6;
  font-size: 14px;
}
.suggestion-row-icon-blue {
  color: #1a73e8;
}
.suggestion-row-text {
  font-size: 15px;
  color: #202124;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.suggestion-row-remove {
  flex-shrink: 0;
  margin-left: auto;
  color: #dadce0;
  font-size: 12px;
  padding: 4px 6px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.15s;
}
.suggestion-row-remove:hover {
  color: #5f6368;
  background: #e8eaed;
}

/* ===== SEE ALL RESULTS ===== */
.suggestion-see-all {
  border-top: 1px solid #ebebeb;
  padding: 12px 16px;
}
.suggestion-see-all-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e8f0fe;
  color: #1967d2;
  font-size: 15px;
}
.suggestion-see-all-text {
  font-size: 14px;
  color: #1967d2;
  font-weight: 500;
}

/* ===== STATES ===== */
.suggestion-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: #70757a;
  font-size: 14px;
}
.suggestion-empty {
  padding: 24px;
  text-align: center;
  color: #70757a;
  font-size: 14px;
}

/* ===== SCROLLBAR ===== */
.suggestions-panel::-webkit-scrollbar { width: 6px; }
.suggestions-panel::-webkit-scrollbar-track { background: transparent; }
.suggestions-panel::-webkit-scrollbar-thumb { background: #dadce0; border-radius: 3px; }
.suggestions-panel::-webkit-scrollbar-thumb:hover { background: #bdc1c6; }

/* ===== UTILITIES ===== */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>